/**
 * membership.ts — 법인 소속(tenants/{t}/members/{uid}) 타입·판정 한 벌 (순수 도메인, firebase 의존 없음).
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 (`tenants/{t}/members/{uid}` 필드 표),
 *       docs/design/10_법인경로_전환설계.md §3 (소속 문서 원형 — backend/functions/src/membership.ts).
 * 통합관리(/group, C)·법인 환경설정 사용자 명단(D)·공간 판정(SpaceContext)이 모두 이 타입을 쓴다.
 * 읽기·쓰기는 services/members.repository.ts 한 곳(2026-10-23 합침 — 옛 domain/tenantMember.ts 폐기).
 *
 * 문서 ID = uid(Firestore 문서 ID 가 곧 사람 키). 같은 사람이 법인마다 한 문서(겸직 = 문서 여러 개).
 * 함수(backend/functions/src/membership.ts)가 만들고, 법인 관리자는 role·biz·dept·position 만 rules 로 직접 바꾼다.
 *
 * ⚠️ 목록을 바꾸면 함께 갱신:
 *   - EMPLOYMENT_VALUES → backend/functions/src/membership.ts EMPLOYMENT_VALUES
 *   - MEMBER_STATUSES   → 함수가 쓰는 status 값("active"·"ended") · firestore.rules members status 판정
 */
import { BIZ_META, TENANT_BIZ, isBizKey, type BizKey, type TenantKey } from "./space";
import type { MemberDeviceSummary } from "./session";

/** 근무 형태 — 런타임 화이트리스트(backend 와 같게) */
export const EMPLOYMENT_VALUES = ["employee", "jiip", "none"] as const;
export type Employment = (typeof EMPLOYMENT_VALUES)[number];
export function isEmployment(v: unknown): v is Employment {
  return typeof v === "string" && (EMPLOYMENT_VALUES as readonly string[]).includes(v);
}
export const EMPLOYMENT_LABEL: Record<Employment, string> = {
  employee: "직원",
  jiip: "지입",
  none: "외부(고용 없음)",
};

export const MEMBER_STATUSES = ["active", "ended"] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];
export function isMemberStatus(v: unknown): v is MemberStatus {
  return typeof v === "string" && (MEMBER_STATUSES as readonly string[]).includes(v);
}

/** tenants/{t}/members/{uid} — 화면에서 쓰는 모양 */
export interface MemberRow {
  /** Firestore 문서 ID = uid */
  id: string;
  uid: string;
  tenant: TenantKey;
  role: string;
  /** 개별 허용·거부 권한 키 */
  allow: string[];
  deny: string[];
  /**
   * 사업부(다중, 2026-10-26) — 그 법인 사업부 중 여러 개(예: 레미콘 + 석산). 준비중 사업부도 들어갈 수 있다.
   * 빈 배열 = 미지정. 옛 문서의 biz(문자열)는 parseMemberBizs 가 [biz] 로 읽는다.
   */
  bizs: BizKey[];
  /**
   * 사업부별 부서(2026-10-26 — 법인 → 사업부 → 부서). 소속 문서 unit_roles = {[biz]: roleKey}, TENANT_BIZ 순서.
   * 옛 문서는 bizs(또는 [biz]) 모두에 role. bizs 는 이 목록의 사업부(파생). role 은 권한용 파생값(authUnitOf).
   */
  units: MemberUnit[];
  /** 통합 직급 사본(users.rank — 함수가 씀). 화면 표시용 */
  rank: string;
  dept: string;
  position: string;
  employment: Employment | null;
  tenantAdmin: boolean;
  /** 모르는 값은 "ended"(권한을 넓히는 쪽으로 잘못 보이지 않게) */
  status: MemberStatus;
  startDate: string;
  /** 종료일(포함). 미래면 "종료 예정" */
  endDate: string;
  /** 종료 효력 시각(종료일 다음날 00:00 KST). rules 와 같은 기준 */
  endAt: Date | null;
  endReason: string;
  /** 사본(함수가 씀) — 없으면 users 문서 값으로 보충 */
  name: string;
  loginId: string;
  lastLoginAt: Date | null;
  devices: MemberDeviceSummary[];
  /**
   * 계정 정지 사본(account_active) — 함수가 user.deactivate/reactivate·set_claims 때 모든 소속 문서에 쓴다.
   * 법인 관리자 명단의 "정지"·[다시 활성] 표시용. 필드가 없으면 null(= 활성으로 표시).
   */
  accountActive: boolean | null;
  /**
   * 보호 표시 사본(protected) — 전체 admin 이거나 어느 법인이든 활성 법인 관리자. 함수(syncMemberStatusCopies)만 쓴다.
   * 법인 관리자는 다른 법인 문서·claims 를 못 읽으므로 화면 잠금에 이 값을 쓴다(보안 판단은 rules·함수). 없으면 null.
   */
  protected: boolean | null;
}

/** 지금 유효한 소속인가 — rules activeMember() 와 같은 기준(status active ∧ end_at 미래 또는 없음) */
export function isMemberActive(m: Pick<MemberRow, "status" | "endAt"> | null | undefined, now = Date.now()): boolean {
  if (!m || m.status !== "active") return false;
  return !m.endAt || m.endAt.getTime() > now;
}

export type MemberPhase = "active" | "ending" | "ended";
/** 표시용 단계 — 재직 / 종료 예정(종료일이 앞날) / 종료 */
export function memberPhase(m: Pick<MemberRow, "status" | "endAt">, now = Date.now()): MemberPhase {
  if (!isMemberActive(m, now)) return "ended";
  return m.endAt ? "ending" : "active";
}
export const MEMBER_PHASE_LABEL: Record<MemberPhase, string> = {
  active: "재직",
  ending: "종료 예정",
  ended: "종료",
};

/**
 * 재직 기간 표시 — 시작일(YYYY-MM-DD)부터 기준일(포함)까지 "N년 M개월" / "M개월" / "N일".
 * 기준일은 오늘 또는 그보다 이른 종료일. 시작일이 없거나 앞날이면 null.
 */
export function tenureText(startDate: string, asOf: string): string | null {
  const s = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate);
  const e = /^(\d{4})-(\d{2})-(\d{2})$/.exec(asOf);
  if (!s || !e || startDate > asOf) return null;
  let months = (Number(e[1]) - Number(s[1])) * 12 + (Number(e[2]) - Number(s[2]));
  if (Number(e[3]) < Number(s[3])) months -= 1;
  if (months < 1) {
    const days = Math.round((Date.UTC(+e[1], +e[2] - 1, +e[3]) - Date.UTC(+s[1], +s[2] - 1, +s[3])) / 86_400_000) + 1;
    return `${days}일`;
  }
  const y = Math.floor(months / 12);
  const m = months % 12;
  return y ? (m ? `${y}년 ${m}개월` : `${y}년`) : `${m}개월`;
}

/** 이 근무 법인에서 직원·지입으로 일하는가(외부 인력 제외). 급여·연차 대상 판정은 급여 명부(employees.companies)가 정본 */
export function isEmployedMember(m: Pick<MemberRow, "employment">): boolean {
  return m.employment === "employee" || m.employment === "jiip";
}

/**
 * 소속 문서의 사업부 목록 — 새 bizs(배열), 옛 biz(문자열)만 있으면 [biz]. 그 법인 사업부만, 중복 없이, TENANT_BIZ 순서.
 * 함수 backend tenantPath.memberBizsOf 와 같은 규칙(런타임 화이트리스트).
 */
export function parseMemberBizs(tenant: TenantKey, d: { bizs?: unknown; biz?: unknown }): BizKey[] {
  const raw: unknown[] = Array.isArray(d.bizs) ? d.bizs : typeof d.biz === "string" ? [d.biz] : [];
  return TENANT_BIZ[tenant].filter((b) => raw.includes(b) && isBizKey(b));
}

export interface MemberUnit {
  biz: BizKey;
  /** 그 사업부의 부서(roles 키) */
  role: string;
}

/** 소속 문서 → 사업부별 부서. backend tenantPath.memberUnitRolesOf 와 같은 규칙(런타임 화이트리스트) */
export function parseUnitRoles(
  tenant: TenantKey,
  d: { unit_roles?: unknown; bizs?: unknown; biz?: unknown; role?: unknown },
): MemberUnit[] {
  const ur = d.unit_roles;
  if (ur && typeof ur === "object" && !Array.isArray(ur)) {
    const o = ur as Record<string, unknown>;
    return TENANT_BIZ[tenant].filter((b) => typeof o[b] === "string" && o[b]).map((b) => ({ biz: b, role: String(o[b]) }));
  }
  const role = typeof d.role === "string" ? d.role : "";
  return parseMemberBizs(tenant, d).map((b) => ({ biz: b, role }));
}

/**
 * 권한을 정하는 사업부 — 권한 판정은 당분간 법인 단위: 첫 '운영 중' 사업부의 부서, 없으면 첫 사업부.
 * (중앙 = 레미콘 부서, 경원 = 아스콘 부서. 석산 부서는 저장·표시만 — 석산 화면을 만들 때 사업부별 권한)
 * backend tenantPath.authUnitOf 와 같은 식.
 */
export function authUnitOf(units: readonly MemberUnit[]): MemberUnit | null {
  return units.find((u) => BIZ_META[u.biz].ready) ?? units[0] ?? null;
}

/** {[biz]: role} 객체(요청 params) */
export function unitRolesObject(units: readonly MemberUnit[]): Record<string, string> {
  return Object.fromEntries(units.map((u) => [u.biz, u.role]));
}

/** 두 사업부별 부서 목록이 같은가(사업부 순서 무관) */
export function sameUnits(a: readonly MemberUnit[], b: readonly MemberUnit[]): boolean {
  return a.length === b.length && a.every((x) => b.some((y) => y.biz === x.biz && y.role === x.role));
}

/** 표시 — "레미콘 · 석산"(준비중 사업부는 그대로 이름만). 없으면 "" */
export function bizsLabel(bizs: readonly BizKey[]): string {
  return bizs.map((b) => BIZ_META[b].label).join(" · ");
}

/** 두 사업부 목록이 같은가(순서 무관) */
export function sameBizs(a: readonly BizKey[], b: readonly BizKey[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}
